import { useState } from "react";
import type { AddressValue, FieldValue, ScalarFieldSpec, ScalarFieldType } from "../types.js";
import type { LookupResult } from "../api/formApi.js";
import { asBoolean, asString, asStringArray, fieldLabel, optionEntries } from "../schema/formModel.js";
import { asAddress } from "../schema/formDesign.js";
import { formatIsNumeric, isFormatPreset } from "../schema/formats.js";
import { AddressInput } from "./AddressInput.js";
import { ChoiceCards } from "./ChoiceCards.js";
import "./styles.css";

export interface ScalarInputProps {
  spec: ScalarFieldSpec;
  id: string;
  value: FieldValue | undefined;
  describedBy?: string;
  invalid?: boolean;
  onChange: (value: FieldValue) => void;
  /** Called when the control loses focus — the page validates the field then (KYC-design). */
  onBlur?: () => void;
  /** KYC-design — an address the client may copy with one tick (`same_as`). */
  sameAs?: { label: string; value: AddressValue } | null;
  /** KYC-design — a value a lookup filled in; shown read-only with a note. */
  prefilled?: boolean;
  prefilledNote?: string;
  /**
   * Phase 541 — the ADDRESS-LOOKUP transport for a field declaring `lookup: postcode`.
   *
   * <p>It is a prop of its OWN rather than the existing `fieldAction` / `fieldSlot` slots,
   * because those are per-TOP-LEVEL-field and are handed over only when the step declares a
   * `lookups:` entry — and an address lookup has neither property: it is declared on the FIELD,
   * it must reach a group SUBFIELD (which is rendered bare), and there is no declaration on the
   * client at all. ABSENT ⇒ no search control, exactly as before this phase.</p>
   */
  onAddressLookup?: (postcode: string) => Promise<LookupResult>;
}

/** Choice groups and addresses take the `<fieldset>` row; everything else a `<label>` row. */
export function rowVariantFor(type: ScalarFieldType | "group"): "field" | "group" | "bare" {
  if (type === "boolean") return "bare"; // the consent card carries its own label
  return type === "radio" || type === "checkbox_group" || type === "group" || type === "address" ? "group" : "field";
}

/** A `select` with up to four options renders as choice cards; longer lists stay a select. */
export const CHOICE_CARD_MAX = 4;

export function ScalarInput({ spec, id, value, describedBy, invalid, onChange, onBlur, sameAs, prefilled, prefilledNote, onAddressLookup }: ScalarInputProps) {
  const required = spec.required ?? false;
  const options = optionEntries(spec).map((entry) => {
    const declared = spec.options?.find((o) => typeof o !== "string" && o.value === entry.value);
    return { ...entry, description: typeof declared === "object" ? declared.description : undefined };
  });
  const mono = isFormatPreset(spec.format);
  const common = {
    id,
    name: id,
    required,
    className: `field-input${mono ? " field-input-mono" : ""}${prefilled ? " field-input-prefilled" : ""}`,
    "aria-describedby": describedBy,
    "aria-invalid": invalid ? true : undefined,
    placeholder: spec.placeholder,
    onBlur,
    readOnly: prefilled,
  } as const;

  switch (spec.type) {
    case "string":
      return (
        <input
          {...common}
          type="text"
          inputMode={mono && isFormatPreset(spec.format) && formatIsNumeric(spec.format) ? "numeric" : undefined}
          value={asString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "email":
      return <input {...common} type="email" autoComplete="email" value={asString(value)} onChange={(e) => onChange(e.target.value)} />;
    case "url":
      return <input {...common} type="url" autoComplete="url" value={asString(value)} onChange={(e) => onChange(e.target.value)} />;
    case "textarea":
      return <textarea {...common} value={asString(value)} onChange={(e) => onChange(e.target.value)} />;
    case "select":
      if (options.length > 0 && options.length <= CHOICE_CARD_MAX) {
        return (
          <ChoiceCards
            id={id}
            options={options}
            selected={asString(value) ? [asString(value)] : []}
            required={required}
            invalid={invalid}
            describedBy={describedBy}
            onSelect={(v) => onChange(v)}
          />
        );
      }
      return (
        <div className="field-select-wrap">
          <select {...common} value={asString(value)} onChange={(e) => onChange(e.target.value)}>
            <option value="">Choose one</option>
            {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>
      );
    case "date":
      return <input {...common} className={`${common.className} field-input-short`} type="date" value={asString(value)} onChange={(e) => onChange(e.target.value)} />;
    case "number":
      return <input {...common} className={`${common.className} field-input-short`} type="number" inputMode="numeric" value={asString(value)} onChange={(e) => onChange(e.target.value)} />;
    case "tel":
      return <input {...common} type="tel" autoComplete="tel" value={asString(value)} onChange={(e) => onChange(e.target.value)} />;
    case "address":
      return (
        <AddressInput
          id={id}
          value={asAddress(value)}
          invalid={invalid}
          describedBy={describedBy}
          onChange={onChange}
          sameAs={sameAs ?? null}
          prefilled={prefilled}
          prefilledNote={prefilledNote}
          lookup={spec.lookup}
          onLookup={onAddressLookup}
        />
      );
    case "boolean": {
      const checked = asBoolean(value);
      return (
        <label
          htmlFor={id}
          className={`consent-card${checked ? " consent-card-checked" : ""}${invalid ? " consent-card-invalid" : ""}`}
        >
          <input
            id={id}
            name={id}
            required={required}
            type="checkbox"
            aria-describedby={describedBy}
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            onBlur={onBlur}
          />
          <span className="consent-mark" aria-hidden="true" />
          <span>{fieldLabel(spec)}</span>
        </label>
      );
    }
    case "radio":
      return (
        <ChoiceCards
          id={id}
          options={options}
          selected={asString(value) ? [asString(value)] : []}
          required={required}
          invalid={invalid}
          describedBy={describedBy}
          onSelect={(v) => onChange(v)}
        />
      );
    case "checkbox_group": {
      const selected = asStringArray(value);
      const declaredValues = options.map((entry) => entry.value);
      return (
        <ChoiceCards
          id={id}
          options={options}
          selected={selected}
          multi
          invalid={invalid}
          describedBy={describedBy}
          onSelect={(v, checked) =>
            onChange(
              checked
                ? declaredValues.filter((o) => o === v || selected.includes(o))
                : selected.filter((o) => o !== v),
            )
          }
        />
      );
    }
    default:
      return null;
  }
}

/** KYC-design — a masked text input with a Show / Hide toggle (`sensitive: true`). */
export function SecretInput(props: ScalarInputProps) {
  const [revealed, setRevealed] = useState(false);
  const { spec, id, value, describedBy, invalid, onChange, onBlur } = props;
  const mono = isFormatPreset(spec.format);
  return (
    <div className="field-secret">
      <input
        id={id}
        name={id}
        required={spec.required ?? false}
        className={`field-input${mono ? " field-input-mono" : ""}`}
        type={revealed ? "text" : "password"}
        autoComplete="off"
        aria-describedby={describedBy}
        aria-invalid={invalid ? true : undefined}
        placeholder={spec.placeholder}
        value={asString(value)}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
      <button type="button" className="field-secret-toggle" onClick={() => setRevealed((r) => !r)} aria-pressed={revealed}>
        {revealed ? "Hide" : "Show"}
      </button>
    </div>
  );
}
